//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { SamplingCampaignRequestQuoteStatus } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { useGetCampaignRequestQuoteQuery } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';
import { SamplingCampaignRequestParams } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsTypes';
import { SamplingCampaignRequestQuoteSummary } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/quote-section/summary/SamplingCampaignRequestQuoteSummary';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { Alert } from '@components/atoms/alert/Alert';
import {
  BodyText,
  SubsectionText,
  TitleText,
} from '@components/atoms/text/Text';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';
import { FormTextInput } from '@components/form/text-input/FormTextInput';
import { Modal } from '@components/molecules/modal/Modal';
import { useCurrentStateAndParams } from '@hooks/useCurrentStateAndParams';
import { isDefined } from '@utils/filterPredicates';
import { optionalReadableMonetaryValue } from '@utils/readableNumber';

interface RequestContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RequestContactModal = ({
  isOpen,
  onClose,
}: RequestContactModalProps) => {
  const { formControl, handleRequestContactCampaign, isRequesting } =
    useSamplingCampaign();

  const { params } = useCurrentStateAndParams<SamplingCampaignRequestParams>();

  const { data: quoteData } = useGetCampaignRequestQuoteQuery(
    {
      campaignRequestId: params.campaignId,
    },
    isDefined(params.campaignId) && isOpen,
  );

  const shouldShowAmount =
    isDefined(quoteData) &&
    quoteData?.status === SamplingCampaignRequestQuoteStatus.QUOTED &&
    isDefined(quoteData.quoteAmount);

  const formattedAmount = optionalReadableMonetaryValue(
    quoteData?.quoteAmount,
    quoteData?.currency,
  );

  return (
    <Modal
      closeModal={onClose}
      contents={
        <Container>
          {shouldShowAmount && (
            <AmountContainer>
              <BodyText>Total Amount</BodyText>
              <TitleText>{formattedAmount}</TitleText>
            </AmountContainer>
          )}
          <QuoteSummaryContainer>
            <SamplingCampaignRequestQuoteSummary />
          </QuoteSummaryContainer>
          <FollowUpSection>
            <SubsectionText>Key Contact</SubsectionText>
            <ContactInfo>
              <FormFieldController control={formControl} name='keyContact.name'>
                {(props) => <FormTextInput label='Name' {...props} />}
              </FormFieldController>
              <FormFieldController
                control={formControl}
                name='keyContact.email'
              >
                {(props) => <FormTextInput label='Email' {...props} />}
              </FormFieldController>
            </ContactInfo>
            <StyledAlert type='info'>
              We endeavor to respond to you within 5 - 7 business days to
              discuss your campaign.
            </StyledAlert>
          </FollowUpSection>
        </Container>
      }
      isFooterEnabled
      isModalOpen={isOpen}
      primaryButtonInfo={{
        buttonContent: 'Request Contact',
        isDisabled: isRequesting,
        onClick: () =>
          handleRequestContactCampaign(
            quoteData?.quoteId,
            quoteData?.externalQuoteId,
          ),
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.REQUEST_CAMPAIGN_QUOTE_CONTACT_MODAL_SUBMIT_BUTTON,
      }}
      secondaryButtonInfo={{
        buttonContent: 'Cancel',
        isDisabled: isRequesting,
        onClick: onClose,
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.REQUEST_CAMPAIGN_QUOTE_CONTACT_MODAL_CANCEL_BUTTON,
      }}
      title='Request Contact'
      width='wide'
    />
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[4]};
`;

const AmountContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacings[1]};
  padding: ${theme.spacings[4]} 0;
  background-color: ${theme.colors.gray00};
  border-radius: ${`${theme.spacings[1]} ${theme.spacings[1]} 0 0`};
`,
);

const QuoteSummaryContainer = styled.div(
  ({ theme }) => `
  padding: ${theme.echo.spacing.sm};
  background-color: ${theme.colors.gray00};
`,
);

const FollowUpSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;

const StyledAlert = styled(Alert)`
  margin-top: ${({ theme }) => theme.spacings[2]};
`;
